/**
 * Banner — primitivo de aviso em faixa (GUPM-273).
 *
 * Extraído do `BannerArea`, que até aqui era o único lugar com essa aparência e
 * só sabia renderizar banner PERSISTIDO (linha em `paebm.listBanners()`). Aviso
 * derivado de estado — "ninguém está de plantão" — não tem linha no banco e nem
 * deveria ter: escrever no banco para avisar sobre o banco cria um segundo dado
 * que pode discordar do primeiro.
 *
 * Aqui fica só a aparência e a semântica de acessibilidade. Quem decide se o
 * aviso aparece é o componente de cima.
 */

import type { ReactNode } from 'react';

export type BannerTone = 'info' | 'aviso' | 'alerta' | 'critico';

/**
 * Tons e ícones vindos do `BannerArea` (severidade do banco), preservados para
 * que banner persistido e banner derivado não fiquem com dois vocabulários
 * visuais para a mesma gravidade.
 */
const TONE: Record<BannerTone, { bg: string; border: string; text: string; icon: string }> = {
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500/40', text: 'text-blue-300', icon: 'ℹ️' },
  aviso: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/40', text: 'text-yellow-300', icon: '⚠️' },
  alerta: { bg: 'bg-orange-500/10', border: 'border-orange-500/40', text: 'text-orange-300', icon: '🚨' },
  critico: { bg: 'bg-red-500/10', border: 'border-red-500/40', text: 'text-red-300', icon: '🆘' },
};

export interface BannerProps {
  tone: BannerTone;
  title: string;
  /** Segunda linha. Aceita nó para permitir link/ênfase sem HTML em string. */
  description?: ReactNode;
  /** Terceira linha, em caixa alta e discreta — origem/norma do aviso. */
  footnote?: string;
  /**
   * Nome acessível. Sem ele o leitor de tela anuncia a região `status` sem dizer
   * do que ela trata — e numa tela com mais de um aviso ficam duas regiões
   * indistinguíveis.
   */
  label?: string;
  className?: string;
}

export function Banner({ tone, title, description, footnote, label, className = '' }: BannerProps) {
  const s = TONE[tone];
  return (
    <div
      role="status"
      aria-label={label}
      className={`rounded-lg border ${s.border} ${s.bg} ${s.text} px-3 py-2 text-sm flex items-start gap-2 ${className}`}
    >
      <span className="text-base shrink-0" aria-hidden>
        {s.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-bold">{title}</div>
        {description && <div className="text-xs opacity-90 mt-0.5">{description}</div>}
        {footnote && (
          <div className="text-[10px] mt-1 opacity-60 uppercase tracking-wide">{footnote}</div>
        )}
      </div>
    </div>
  );
}
